import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const slug = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Közös váz a jogi oldalakhoz (ToS, Rules, Privacy).
 *
 * A tartalomjegyzéket futásidőben a kirenderelt <h2>-kből építi, így egyik
 * oldalon sem kell külön karbantartani egy szakaszlistát — ha új szakasz kerül
 * a szövegbe, magától megjelenik a jegyzékben is.
 */
export default function LegalLayout({ title, lastUpdated, intro, children, back }) {
  const contentRef = useRef(null);
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const headings = Array.from(root.querySelectorAll('h2'));
    headings.forEach(h => {
      if (!h.id) h.id = slug(h.textContent || '');
      h.classList.add('scroll-mt-nav');
    });
    setToc(headings.map(h => ({ id: h.id, text: h.textContent })));

    if (typeof IntersectionObserver === 'undefined') return;

    // Az a szakasz az aktív, amelyik legfelül még látszik a nézetben.
    const io = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-88px 0px -70% 0px', threshold: 0 }
    );

    headings.forEach(h => io.observe(h));
    return () => io.disconnect();
  }, [children]);

  return (
    <div className="page-container max-w-6xl">
      {/* Fejléc */}
      <header className="max-w-3xl mb-10">
        <span className="eyebrow mb-4">Legal</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
          {title}
        </h1>
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                           bg-white/[0.04] border border-white/[0.08] text-xs text-gray-500">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                 strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            Last updated: {lastUpdated}
          </span>
        </div>
        {intro && (
          <div className="text-sm text-gray-400 leading-relaxed mt-5 space-y-3">{intro}</div>
        )}
      </header>

      <div className="grid lg:grid-cols-[15rem_1fr] gap-10">
        {/* Tartalomjegyzék */}
        <aside className="hidden lg:block">
          <nav className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
              On this page
            </p>
            <ul className="space-y-0.5 border-l border-white/[0.07]">
              {toc.map(item => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={`block -ml-px pl-4 py-1.5 text-[13px] leading-snug border-l
                                transition-colors duration-150 ${
                      activeId === item.id
                        ? 'border-brand-400 text-brand-200'
                        : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-white/20'
                    }`}
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Tartalom */}
        <div className="min-w-0">
          <div ref={contentRef} className="card p-6 sm:p-8 space-y-9 text-sm text-gray-300 leading-relaxed">
            {children}
          </div>

          {back && (
            <div className="text-center mt-8">
              <Link
                to={back.to}
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-300
                           hover:text-brand-200 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M19 12H5M11 18l-6-6 6-6" />
                </svg>
                {back.label}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Számozott szakasz — a <h2> egyben a tartalomjegyzék horgonya is. */
export function LegalSection({ title, children, spacing = 'space-y-3' }) {
  return (
    <section>
      <h2 className="font-display text-base font-semibold text-white mb-3 tracking-tight">
        {title}
      </h2>
      <div className={spacing}>{children}</div>
    </section>
  );
}

export function LegalSubSection({ title, children }) {
  return (
    <div className="mt-5">
      <h3 className="text-sm font-semibold text-gray-200 mb-2.5">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
